// @/utils/analytics/tracking.js

/**
 * Common Tracking Utilities
 * 
 * Predefined tracking functions for common platform actions.
 * These provide consistent event naming and properties across the app.
 * 
 * Usage:
 * import { trackUserAction, trackFeatureUsage } from '@/utils/analytics/tracking';
 * trackUserAction('login', { method: 'email' });
 */

import { postHogService } from '@/services/analytics/posthog';

/**
 * Track user authentication actions
 */
export const trackAuth = {
  login: (method = 'email') => {
    postHogService.trackEvent('user_login', {
      method,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Track navigation and page interactions
 */
export const trackNavigation = {
  pageView: (page, properties = {}) => {
    postHogService.trackPageView(window.location.href, {
      page_name: page,
      ...properties
    });
  }
};

/**
 * Track feature usage across different modules
 */
export const trackFeature = {
  // Overview / Customer Requests
  customerRequest: {
    statusChange: (page, newStatus, oldStatus) => {
      postHogService.trackEvent('invitation_status_change', {
        page: page,
        new_status: newStatus,
        old_status: oldStatus
      });
    }
  },

  // Self Assessment
  selfAssessment: {
    saved: (section, questionCount) => {
      postHogService.trackFeatureUsage('self_assessment', 'saved', {
        section,
        question_count: questionCount
      });
    },
    toggleTracking: (page, section, statusBefore, statusAfter) => {
      postHogService.trackEvent('organization_overview_toggle', {
        page,
        section,
        status_before: statusBefore,
        status_after: statusAfter
      });
    }
  },

  // Carbon Accounting
  carbonAccounting: {
    calculated: (page, year, calculationTime, firstTime = false) => {
      postHogService.trackEvent('carbon_footprint_calculate', {
        page,
        year,
        calculation_time: calculationTime,
        first_time: firstTime
      });
    }
  },

  // Supply Chain Management
  supplyChain: {
    supplierInvite: (action, page) => {
      postHogService.trackEvent('supplier_invite', {
        action,
        page
      });
    },
    tabSwitch: (tabId) => {
      postHogService.trackEvent('scm_tab_switch', {
        tab: tabId,
        timestamp: new Date().toISOString()
      });
    },
    filterApplied: (filterType, filterValues) => {
      postHogService.trackEvent('scm_filter_applied', {
        filter_type: filterType,
        filter_values: filterValues
      });
    },
    filterCleared: (filterType) => {
      postHogService.trackEvent('scm_filter_cleared', {
        filter_type: filterType || 'all'
      });
    },
    filterOverlayOpened: () => {
      postHogService.trackEvent('scm_filter_overlay_opened', {});
    },
    sortApplied: (column, direction) => {
      postHogService.trackEvent('scm_sort_applied', {
        column,
        direction
      });
    }
  },

  // Engagement email templates (Phase 1 of Mailqueue 3.0).
  // Spec: Notion 2b2a8634c7bf80c18b0bfe2cc9ebf2e4 §tracking.
  emailTemplates: {
    configured: ({ templateId, templateType, projectId } = {}) => {
      postHogService.trackEvent('email_template_configured', {
        template_id: templateId,
        template_type: templateType,
        project_id: projectId,
      });
    },
    duplicated: ({ sourceTemplateId, newTemplateId, templateType, projectId } = {}) => {
      postHogService.trackEvent('email_template_duplicated', {
        source_template_id: sourceTemplateId,
        new_template_id: newTemplateId,
        template_type: templateType,
        project_id: projectId,
      });
    },
    deleted: ({ stage, templateId, templateType, projectId } = {}) => {
      // stage: 'initiated' | 'confirmed' | 'cancelled'
      postHogService.trackEvent('email_template_deleted', {
        stage,
        template_id: templateId,
        template_type: templateType,
        project_id: projectId,
      });
    },
    previewed: ({ templateId, language, projectId } = {}) => {
      postHogService.trackEvent('email_template_previewed', {
        template_id: templateId,
        language,
        project_id: projectId,
      });
    },
    batchSent: ({ stage, mode, supplierCount, templateId, templateType, projectId } = {}) => {
      // stage: 'initiated' | 'confirmed' | 'cancelled' | 'sent'
      // mode:  'reminder'  | 'invitation'
      postHogService.trackEvent('email_batch_sent', {
        stage,
        mode,
        supplier_count: supplierCount,
        template_id: templateId,
        template_type: templateType,
        project_id: projectId,
      });
    },
  },

  // Account Settings
  settings: {
    languageChange: (from, to) => {
      postHogService.trackEvent('language_change', {
        old_language: from,
        new_language: to
      });
    }
  }
};

/**
 * Track user engagement and interaction patterns
 */
export const trackEngagement = {
  sessionStart: () => {
    postHogService.trackEngagement('session_start', {
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`
    });
  },

  sessionEnd: (duration) => {
    postHogService.trackEngagement('session_end', {
      duration_ms: duration,
      timestamp: new Date().toISOString()
    });
  },

  buttonClick: (buttonName, context) => {
    postHogService.trackEngagement('button_click', {
      button_name: buttonName,
      context,
      timestamp: new Date().toISOString()
    });
  },

  formSubmitted: (formName, success = true) => {
    postHogService.trackEngagement('form_submission', {
      form_name: formName,
      success,
      timestamp: new Date().toISOString()
    });
  },

  errorEncountered: (errorType, context) => {
    postHogService.trackEngagement('error_encountered', {
      error_type: errorType,
      context,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Track business-critical actions for analytics
 */
export const trackBusiness = {
  assessmentCompleted: (type, completionRate) => {
    postHogService.trackEvent('assessment_completed', {
      type,
      completion_rate: completionRate,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Generic tracking function for custom events
 */
export const trackCustomEvent = (eventName, properties = {}) => {
  postHogService.trackEvent(eventName, {
    ...properties,
    timestamp: new Date().toISOString()
  });
};

// Export everything as default for convenience
export default {
  trackAuth,
  trackNavigation,
  trackFeature,
  trackEngagement,
  trackBusiness,
  trackCustomEvent
};